/* ⚠️ GENERATED from app/services/commentPages.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   💬 services/commentPages.ts — a post's comments, a page at a time.

   🟥 WHY. Every comment used to live INSIDE the post document, as one array.
      A Firestore document is capped at 1 MiB, so a post stopped accepting
      comments somewhere around three to five thousand of them — and long
      before that, every feed load downloaded every comment of every post, one
      document took every comment write (Firestore sustains ~1 write/second on
      one document), and opening the sheet parsed the whole lot on the JS
      thread at once.

   🟢 SO A NEW COMMENT IS ITS OWN DOCUMENT, written by the server only:
        posts/{postId}/comments/{id}   — top-level comments (`rank` for Top)
        posts/{postId}/replies/{id}    — replies, `parentId` = their thread
      The post keeps `commentsCount` (the server's running total, old array
      included) and `commentsV2: true` from the first such comment on. Two
      collections rather than one so every query the server runs is served by
      Firestore's automatic single-field indexes — nothing to deploy by hand.

   🟢 OLD COMMENTS STAY WHERE THEY ARE. The array is read exactly as before and
      merged with the pages; nothing is migrated, nothing is lost, and an old
      post with no new comments behaves exactly as it always did.

   🟢 THE SAME RULES ON BOTH CLIENTS (and the server's paging helpers), generated
      by tools/gen-twins.mjs — so the phone and the browser agree about when a
      skeleton shows, what "more" means and which old comments wait their turn.
   ═══════════════════════════════════════════════════════════════════════════ */
import { commentsOf, commentMs, commentScore, upsertComment, dedupeComments, mergeLocalComments, commentSortOf, commentCountOf } from './vibeComments';
/** Threads per page — about two screens, so the next page is asked for long before the end. */
export const COMMENT_PAGE_SIZE = 20;
/** The most a client may ask for in one page. */
export const COMMENT_PAGE_MAX = 50;
/** Replies fetched alongside one page of threads. */
export const COMMENT_REPLIES_MAX = 400;
/** Newer comments fetched in one "since" call. */
export const COMMENT_SINCE_MAX = 100;
/** Skeleton rows on a first open — never more rows than the post has comments. */
export const COMMENT_SKELETON_MAX = 6;
/** Skeleton rows under the list while the next page is on its way. */
export const COMMENT_FOOTER_SKELETONS = 3;
/** FlatList `onEndReachedThreshold`, in screen heights: ask early, rarely be seen waiting. */
export const COMMENT_END_THRESHOLD = 1.5;
/** At most one "what is new" call per this many ms while comments pour in. */
export const COMMENT_SINCE_GAP_MS = 1500;
/** A first page that has not answered in this long is shown as an error with Retry. */
export const COMMENT_FIRST_TIMEOUT_MS = 9000;

/** Does this post keep (some of) its comments as documents? */
export function isPagedPost(post) {
  return !!post && post.commentsV2 === true;
}

/* ═══ ORDER ═══════════════════════════════════════════════════════════════
   🟢 TOP IS ONE NUMBER, so the server can ask Firestore for it with a single-
      field index: agreement first, and — buildThreads' own tie-break — the
      OLDER comment first among equals. Seconds, not ms, so the number stays an
      exact integer (score × 1e10 stays under 2^53 up to ±900,000). */
const RANK_SCORE_CAP = 900000;
export function commentRank(c) {
  const score = Math.max(-RANK_SCORE_CAP, Math.min(RANK_SCORE_CAP, commentScore(c)));
  const sec = Math.max(0, Math.min(9999999999, Math.floor(commentMs(c && c.timestamp) / 1000)));
  return score * 1e10 + (9999999999 - sec);
}

/** The server's order for a sort: the field, descending. */
export function orderFieldOf(sort) {
  return commentSortOf(sort) === 'newest' ? 'timestamp' : 'rank';
}

/** -1 when `a` comes before `b` in the server's order, 1 after, 0 same place. */
export function serverOrder(a, b, sort) {
  const ka = commentSortOf(sort) === 'newest' ? commentMs(a && a.timestamp) : commentRank(a);
  const kb = commentSortOf(sort) === 'newest' ? commentMs(b && b.timestamp) : commentRank(b);
  if (ka !== kb) return ka > kb ? -1 : 1;
  const ia = String(a && a.id || '');
  const ib = String(b && b.id || '');
  return ia === ib ? 0 : ia > ib ? -1 : 1;
}

/* ═══ CURSORS (server) ════════════════════════════════════════════════════ */
/** The last thread of a page → where the next one starts. */
export function cursorOf(c, sort) {
  if (!c || !c.id) return null;
  const v = orderFieldOf(sort) === 'timestamp' ? commentMs(c.timestamp) : commentRank(c);
  return `${String(c.id)}~${v}`;
}
export function parseCursor(s) {
  const m = /^([^~]{1,128})~(-?\d{1,20}(?:\.\d+)?)$/.exec(String(s || ''));
  if (!m) return null;
  const value = Number(m[2]);
  return Number.isFinite(value) ? {
    id: m[1],
    value
  } : null;
}
export function pageLimitOf(v) {
  const n = Math.floor(Number(v) || COMMENT_PAGE_SIZE);
  return Math.max(1, Math.min(COMMENT_PAGE_MAX, n));
}
export function chunk(arr, n) {
  const out = [];
  for (let i = 0; i < (arr || []).length; i += n) out.push(arr.slice(i, i + n));
  return out;
}
/** A stored comment as the wire carries it: ms timestamps, nothing server-only. */
export function wireComment(c) {
  if (!c || typeof c !== 'object') return null;
  const out = {
    ...c,
    timestamp: commentMs(c.timestamp)
  };
  delete out.rank;
  delete out.legacyParent;
  delete out.postId;
  return out;
}

/* ═══ THE CLIENT'S PAGES ══════════════════════════════════════════════════ */

export function emptyPages() {
  return {
    items: [],
    next: null,
    done: false,
    loading: null,
    error: false,
    started: false,
    ready: false,
    seen: 0,
    newestMs: 0
  };
}
export function pagesKey(postId, sort) {
  return `${String(postId || '')}|${commentSortOf(sort)}`;
}
function newestOf(items, prev) {
  let m = prev || 0;
  for (const c of items || []) {
    const t = commentMs(c && c.timestamp);
    if (t > m) m = t;
  }
  return m;
}
export function beginLoad(s, kind, seen) {
  return {
    ...s,
    loading: kind,
    started: s.started || kind === 'first' || kind === 'more',
    error: kind === 'first' || kind === 'more' ? false : s.error,
    seen: typeof seen === 'number' ? seen : s.seen
  };
}
export function pageArrived(s, kind, res) {
  let items = s.items;
  for (const c of res.items || []) if (c && c.id) items = upsertComment(items, c);
  const paging = kind === 'first' || kind === 'more';
  return {
    ...s,
    items,
    loading: null,
    error: false,
    ready: s.ready || kind === 'first',
    next: paging ? res.next || null : s.next,
    done: paging ? !!res.done || !res.next : s.done,
    newestMs: newestOf(res.items || [], s.newestMs)
  };
}
export function pageFailed(s, kind) {
  return {
    ...s,
    loading: null,
    error: kind === 'first' || kind === 'more' ? true : s.error
  };
}
/** My own new or edited comment, or one the server just confirmed. */
export function absorbComment(s, c) {
  if (!c || !c.id) return s;
  return {
    ...s,
    items: upsertComment(s.items, c),
    newestMs: newestOf([c], s.newestMs)
  };
}
/** Removed comments (a delete takes its replies with it — pass all the ids). */
export function dropComments(s, ids) {
  const gone = new Set((ids || []).map(String));
  if (!gone.size) return s;
  const items = s.items.filter(c => !gone.has(String(c && c.id || '')) && !gone.has(String(c && c.parentId || '')));
  return items.length === s.items.length ? s : {
    ...s,
    items
  };
}
export function wantsFirst(post, s) {
  return isPagedPost(post) && !s.started && !s.loading;
}
export function wantsMore(post, s) {
  return isPagedPost(post) && s.ready && !s.loading && !s.done && !s.error && !!s.next;
}
/** The counter moved past what we last asked about: somebody else commented. */
export function wantsSince(post, s) {
  return isPagedPost(post) && s.ready && !s.loading && commentCountOf(post) > s.seen;
}

/* ═══ WHAT THE SHEET SHOWS ════════════════════════════════════════════════
   🟥 AN OLD COMMENT WAITS FOR ITS TURN. The legacy array arrives whole; the
      pages arrive twenty threads at a time. Shown together naively, the old
      comments would sit at the bottom of "Newest" until the next page landed
      ABOVE them — the list rearranging itself under the reader's thumb. So,
      while pages remain, an old thread is shown only once the pages have
      reached its place in the order. Your own are always shown.
   🟥 AND A REPLY NEVER SHOWS WITHOUT ITS THREAD. buildThreads promotes an
      orphan to the top level (right for a deleted parent), so a reply whose
      thread is not loaded YET is held back instead of shown as a stray. */
export function sheetComments(post, s, opts) {
  /* 📌 the owner's pinned comment leads — so it is never held back to wait for its place in the order */
  const pinnedId = post && post.pinnedCommentId ? String(post.pinnedCommentId) : '';
  const legacy = commentsOf(post);
  const local = opts && opts.local || null;
  if (!isPagedPost(post)) return mergeLocalComments(legacy, local);
  const st = s || emptyPages();
  const pin = opts && opts.pinUid ? String(opts.pinUid) : '';
  const sort = commentSortOf(opts && opts.sort);
  const pagedRoots = st.items.filter(c => c && !c.parentId);
  /* the place the pages have reached is the cursor itself — not the last row
     held, which a "since" or a link may have added out of turn */
  const reached = st.done ? null : parseCursor(st.next);
  const keepRoot = c => {
    if (pin && String(c.userId) === pin) return true;
    if (pinnedId && String(c.id) === pinnedId) return true;
    if (!st.ready) return false; /* nothing until the first page — then all in order */
    if (!reached) return true;
    const k = sort === 'newest' ? commentMs(c.timestamp) : commentRank(c);
    return k >= reached.value;
  };
  const roots = new Set();
  const out = [];
  for (const c of legacy) if (!c.parentId && keepRoot(c)) {
    out.push(c);
    if (c.id) roots.add(String(c.id));
  }
  for (const c of pagedRoots) {
    out.push(c);
    roots.add(String(c.id));
  }
  const known = new Set([...legacy, ...st.items].map(c => String(c && c.id || '')).filter(Boolean));
  for (const c of [...legacy, ...st.items]) {
    if (!c || !c.parentId) continue;
    const p = String(c.parentId);
    /* a reply whose parent is gone for good (not merely unloaded) is promoted, as before */
    if (roots.has(p) || !known.has(p) && st.done) out.push(c);
  }
  const mine = (local || []).filter(c => c && (!c.parentId || roots.has(String(c.parentId)) || known.has(String(c.parentId))));
  return mergeLocalComments(dedupeComments(out), mine);
}

/**
 * What the comment sheet is showing right now.
 *   list      — rows (with `footer` skeleton rows while more are coming)
 *   skeleton  — `rows` placeholder rows: there ARE comments and we have none yet
 *   empty     — there are no comments, said at once (no skeleton for nothing)
 *   offline   — none to show and no connection
 *   error     — the first page failed; Retry
 * 🟥 NO SKELETON OVER CONTENT. If anything can be shown, it is shown.
 */

export function commentSheetPlan(o) {
  const st = o.state || emptyPages();
  const paged = isPagedPost(o.post);
  const count = commentCountOf(o.post);
  const moreComing = paged && st.ready && !st.done;
  if (o.shown > 0) {
    return {
      mode: 'list',
      rows: 0,
      footer: moreComing && !st.error ? COMMENT_FOOTER_SKELETONS : 0,
      moreFailed: moreComing && st.error
    };
  }
  const waiting = paged ? !st.ready && !st.error : !!o.docPending;
  if (count > 0 && waiting && !o.offline) return {
    mode: 'skeleton',
    rows: Math.max(1, Math.min(COMMENT_SKELETON_MAX, count)),
    footer: 0,
    moreFailed: false
  };
  if (o.offline && (count > 0 || o.docPending)) return {
    mode: 'offline',
    rows: 0,
    footer: 0,
    moreFailed: false
  };
  if (paged && st.error && count > 0) return {
    mode: 'error',
    rows: 0,
    footer: 0,
    moreFailed: false
  };
  return {
    mode: 'empty',
    rows: 0,
    footer: 0,
    moreFailed: false
  };
}

/* ═══ THE STORE ═══════════════════════════════════════════════════════════
   One per client, outside React, so the pages survive the sheet closing: open
   the same post again and its comments are there at once, no skeleton. The
   fetcher is the platform's own (auth + backend URL differ); everything else
   is here, once. */

export function createCommentPager(fetcher, opts) {
  const now = opts && opts.now || (() => Date.now());
  const timeoutMs = opts && opts.timeoutMs || COMMENT_FIRST_TIMEOUT_MS;
  const maxPosts = opts && opts.maxPosts || 40;
  const states = new Map();
  const subs = new Set();
  const lastSince = new Map();
  const emit = () => {
    for (const f of Array.from(subs)) {
      try {
        f();
      } catch (e) {/* a listener's own problem */}
    }
  };
  /* one shared "nothing yet" — a fresh object per read would look like a change to every reader */
  const NONE = emptyPages();
  const get = (postId, sort) => states.get(pagesKey(postId, sort)) || NONE;
  const put = (key, s) => {
    states.delete(key);
    states.set(key, s);
    /* 🟢 BOUNDED: the oldest posts' pages go first — a long scroll through the
       feed must not keep every thread ever opened in memory */
    while (states.size > maxPosts * 2) {
      const k = states.keys().next().value;
      if (k === undefined) break;
      states.delete(k);
    }
    emit();
  };
  const withTimeout = p => new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('timeout')), timeoutMs);
    p.then(v => {
      clearTimeout(t);
      resolve(v);
    }, e => {
      clearTimeout(t);
      reject(e);
    });
  });
  async function run(post, sort, kind, q) {
    const postId = String(post && post.id || '');
    if (!postId) return;
    const key = pagesKey(postId, sort);
    put(key, beginLoad(get(postId, sort), kind, kind === 'first' || kind === 'since' ? commentCountOf(post) : undefined));
    let res = null;
    try {
      res = await withTimeout(fetcher({
        postId,
        sort: commentSortOf(sort),
        limit: COMMENT_PAGE_SIZE,
        ...q
      }));
    } catch (e) {
      res = null;
    }
    const cur = get(postId, sort);
    if (res && res.ok) put(key, pageArrived(cur, kind, res));else put(key, pageFailed(cur, kind));
  }
  return {
    get,
    subscribe(fn) {
      subs.add(fn);
      return () => {
        subs.delete(fn);
      };
    },
    /** Ask for whatever this post needs next: the first page, or what is new. */
    sync(post, sort) {
      const s = get(post && post.id, sort);
      if (wantsFirst(post, s)) return run(post, sort, 'first', {});
      if (wantsSince(post, s)) {
        const key = pagesKey(post.id, sort);
        const t = now();
        if (t - (lastSince.get(key) || 0) < COMMENT_SINCE_GAP_MS) return undefined;
        lastSince.set(key, t);
        return run(post, sort, 'since', {
          since: s.newestMs
        });
      }
      return undefined;
    },
    more(post, sort) {
      const s = get(post && post.id, sort);
      if (!wantsMore(post, s)) return undefined;
      return run(post, sort, 'more', {
        cursor: s.next
      });
    },
    retry(post, sort) {
      const s = get(post && post.id, sort);
      if (s.loading) return undefined;
      if (!s.ready) {
        put(pagesKey(post.id, sort), emptyPages());
        return run(post, sort, 'first', {});
      }
      return run(post, sort, 'more', {
        cursor: s.next
      });
    },
    /** A link or a bell row aimed at a comment that may not be loaded. */
    focus(post, sort, commentId) {
      if (!isPagedPost(post) || !commentId) return undefined;
      const s = get(post.id, sort);
      if (s.items.some(c => String(c.id) === String(commentId))) return undefined;
      return run(post, sort, 'focus', {
        focus: String(commentId)
      });
    },
    absorb(postId, c) {
      const id = String(postId || '');
      for (const k of Array.from(states.keys())) if (k.startsWith(`${id}|`)) states.set(k, absorbComment(states.get(k), c));
      emit();
    },
    drop(postId, ids) {
      const id = String(postId || '');
      for (const k of Array.from(states.keys())) if (k.startsWith(`${id}|`)) states.set(k, dropComments(states.get(k), ids));
      emit();
    },
    /** Every comment this client holds for a post, any sort — for links and deletes. */
    allFor(postId) {
      const id = String(postId || '');
      let out = [];
      for (const [k, s] of Array.from(states.entries())) if (k.startsWith(`${id}|`)) out = out.concat(s.items);
      return dedupeComments(out);
    },
    _reset() {
      states.clear();
      lastSince.clear();
      emit();
    }
  };
}
